// Decode only -- signature verification is the backend's job. The token is
// opaque to this app beyond the claims it reads.

import type { Principal } from "./authorities";

function base64UrlDecode(input: string): string {
  let output = input.replace(/-/g, "+").replace(/_/g, "/");
  switch (output.length % 4) {
    case 0:
      break;
    case 2:
      output += "==";
      break;
    case 3:
      output += "=";
      break;
    default:
      throw new Error("Illegal base64url string!");
  }
  // atob gives latin1; re-decode as UTF-8 so non-ASCII claims survive.
  const binary = atob(output);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

export function decodeToken(token: string): Record<string, unknown> {
  const parts = token.split(".");
  if (parts.length !== 3) {
    throw new Error(
      "The inspected token doesn't appear to be a JWT. " +
        "Check to make sure it has three parts and see https://jwt.io for more.",
    );
  }
  return JSON.parse(base64UrlDecode(parts[1]));
}

/**
 * Decode into the claim shape the app uses. Returns null for anything
 * unusable rather than throwing -- a corrupt token in storage must land the
 * user on the login page, not on a blank screen.
 */
export function readPrincipal(token: string | null): Principal | null {
  if (!token) return null;
  let claims: Record<string, unknown>;
  try {
    claims = decodeToken(token);
  } catch {
    return null;
  }
  const authorities = Array.isArray(claims.authorities) ? (claims.authorities as string[]) : [];
  const roles = Array.isArray(claims.roles) ? (claims.roles as string[]) : [];
  const userId = typeof claims.user_name === "string" ? claims.user_name : "";
  return {
    authorities,
    roles,
    userId,
    email: typeof claims.email === "string" ? claims.email : undefined,
    exp: typeof claims.exp === "number" ? claims.exp : undefined,
  };
}

export function tokenExpiry(token: string | null): Date | null {
  const principal = readPrincipal(token);
  if (!principal?.exp) return null;
  return new Date(principal.exp * 1000);
}

export function isTokenExpired(token: string | null, offsetSeconds = 0): boolean {
  if (!token) return true;
  const expiry = tokenExpiry(token);
  if (expiry === null) return true;
  return !(expiry.valueOf() > Date.now() + offsetSeconds * 1000);
}
